import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeftRight, FileCode, FileText, Copy, Download, Trash2 } from 'lucide-react';
import { useSEO } from '../hooks/useSEO';
import { toast } from 'react-hot-toast';
import AdContainer from '../components/ads/AdContainer';
import { GOOGLE_ADSENSE_CONFIG } from '../config/adsConfig';

const JsonCsvConverter: React.FC = () => {
  const [jsonInput, setJsonInput] = useState('');
  const [csvInput, setCsvInput] = useState('');
  const [jsonOutput, setJsonOutput] = useState('');
  const [csvOutput, setCsvOutput] = useState('');
  const [activeTab, setActiveTab] = useState<'json-to-csv' | 'csv-to-json'>('json-to-csv');

  useSEO({
    title: 'JSON to CSV Converter - Convert JSON to CSV & CSV to JSON Online',
    description: 'Free online JSON to CSV converter. Convert JSON arrays to CSV format and CSV files to JSON. Bidirectional conversion with proper formatting.',
    keywords: 'JSON to CSV, CSV to JSON, JSON converter, CSV converter, JSON CSV conversion, data format converter',
    canonical: 'https://snap-tools.online/dev/json-csv-converter'
  });

  const jsonToCsv = () => {
    try {
      const data = JSON.parse(jsonInput);
      
      if (!Array.isArray(data)) {
        throw new Error('JSON must be an array of objects');
      }
      
      if (data.length === 0) {
        setCsvOutput('');
        return;
      }

      // Get all unique keys from all objects
      const allKeys = [...new Set(data.flatMap(obj => Object.keys(obj)))];
      
      // Create CSV header
      const header = allKeys.join(',');
      
      // Create CSV rows
      const rows = data.map(obj => 
        allKeys.map(key => {
          const value = obj[key];
          if (value === null || value === undefined) return '';
          if (typeof value === 'string' && (value.includes(',') || value.includes('"') || value.includes('\n'))) {
            return `"${value.replace(/"/g, '""')}"`;
          }
          return String(value);
        }).join(',')
      );
      
      const csv = [header, ...rows].join('\n');
      setCsvOutput(csv);
      toast.success('JSON converted to CSV');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Invalid JSON format');
      setCsvOutput('');
    }
  };

  const csvToJson = () => {
    try {
      const lines = csvInput.trim().split('\n');
      if (lines.length < 2) {
        throw new Error('CSV must have at least a header and one data row');
      }

      const headers = parseCSVLine(lines[0]);
      const data = lines.slice(1).map(line => {
        const values = parseCSVLine(line);
        const obj: Record<string, any> = {};
        
        headers.forEach((header, index) => {
          const value = values[index] || '';
          // Try to parse as number or boolean
          if (value === 'true') obj[header] = true;
          else if (value === 'false') obj[header] = false;
          else if (value === '') obj[header] = null;
          else if (!isNaN(Number(value)) && value !== '') obj[header] = Number(value);
          else obj[header] = value;
        });
        
        return obj;
      });

      const json = JSON.stringify(data, null, 2);
      setJsonOutput(json);
      toast.success('CSV converted to JSON');
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Invalid CSV format');
      setJsonOutput('');
    }
  };

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let inQuotes = false;
    
    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      const nextChar = line[i + 1];
      
      if (char === '"' && inQuotes && nextChar === '"') {
        current += '"';
        i++; // Skip next quote
      } else if (char === '"') {
        inQuotes = !inQuotes;
      } else if (char === ',' && !inQuotes) {
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
    
    result.push(current);
    return result;
  };

  const copyOutput = () => {
    const output = activeTab === 'json-to-csv' ? csvOutput : jsonOutput;
    navigator.clipboard.writeText(output).then(() => {
      toast.success('Output copied to clipboard');
    });
  };

  const downloadOutput = () => {
    const output = activeTab === 'json-to-csv' ? csvOutput : jsonOutput;
    const filename = activeTab === 'json-to-csv' ? 'converted.csv' : 'converted.json';
    const mimeType = activeTab === 'json-to-csv' ? 'text/csv' : 'application/json';
    
    const blob = new Blob([output], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`${filename} downloaded`);
  };

  const loadSample = () => {
    if (activeTab === 'json-to-csv') {
      const sample = [
        { "name": "John Doe", "age": 30, "city": "New York", "active": true },
        { "name": "Jane Smith", "age": 25, "city": "Los Angeles", "active": false },
        { "name": "Bob Johnson", "age": 35, "city": "Chicago", "active": true }
      ];
      setJsonInput(JSON.stringify(sample, null, 2));
    } else {
      const sample = `name,age,city,active
John Doe,30,New York,true
Jane Smith,25,Los Angeles,false
Bob Johnson,35,Chicago,true`;
      setCsvInput(sample);
    }
    toast.success('Sample data loaded');
  };

  const clearInputs = () => {
    setJsonInput('');
    setCsvInput('');
    setJsonOutput('');
    setCsvOutput('');
    toast.success('All fields cleared');
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 sm:py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8 sm:mb-12"
        >
          <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-r from-[#8438ff] to-[#7228ee] rounded-xl sm:rounded-2xl mb-4 sm:mb-6 shadow-lg">
            <ArrowLeftRight className="h-7 w-7 sm:h-8 sm:w-8 text-white" />
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#8438ff] mb-3 sm:mb-4">
            JSON ↔ CSV Converter
          </h1>
          <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
            Convert JSON arrays to CSV format and CSV files to JSON. Bidirectional conversion 
            with intelligent data type handling and proper formatting.
          </p>
        </motion.div>

        {/* Header Ad */}
        <AdContainer 
          position="header" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.header}
          className="mb-6 sm:mb-8"
        />

        {/* Tab Navigation */}
        <div className="flex justify-center mb-6 sm:mb-8">
          <div className="bg-white rounded-lg p-1 shadow-lg">
            <button
              onClick={() => setActiveTab('json-to-csv')}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                activeTab === 'json-to-csv'
                  ? 'bg-[#8438ff] text-white'
                  : 'text-gray-600 hover:text-[#8438ff]'
              }`}
            >
              <FileCode className="inline h-4 w-4 mr-2" />
              JSON to CSV
            </button>
            <button
              onClick={() => setActiveTab('csv-to-json')}
              className={`px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                activeTab === 'csv-to-json'
                  ? 'bg-[#8438ff] text-white'
                  : 'text-gray-600 hover:text-[#8438ff]'
              }`}
            >
              <FileText className="inline h-4 w-4 mr-2" />
              CSV to JSON
            </button>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {/* Input Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Input {activeTab === 'json-to-csv' ? 'JSON' : 'CSV'}
                </h3>
                <div className="flex space-x-1 sm:space-x-2">
                  <button
                    onClick={loadSample}
                    className="px-3 py-1.5 text-xs sm:text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    Sample
                  </button>
                  <button
                    onClick={clearInputs}
                    className="p-1.5 sm:p-2 text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                    title="Clear all"
                  >
                    <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                </div>
              </div>

              <textarea
                value={activeTab === 'json-to-csv' ? jsonInput : csvInput}
                onChange={(e) => activeTab === 'json-to-csv' ? setJsonInput(e.target.value) : setCsvInput(e.target.value)}
                placeholder={activeTab === 'json-to-csv' ? 'Paste your JSON array here...' : 'Paste your CSV data here...'}
                className="w-full h-64 sm:h-80 lg:h-96 p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl resize-none focus:ring-2 focus:ring-[#8438ff] focus:border-[#8438ff] transition-colors font-mono text-xs sm:text-sm"
              />

              <div className="mt-3 sm:mt-4">
                <button
                  onClick={activeTab === 'json-to-csv' ? jsonToCsv : csvToJson}
                  disabled={!(activeTab === 'json-to-csv' ? jsonInput : csvInput).trim()}
                  className="w-full px-4 py-3 bg-[#8438ff] text-white rounded-lg hover:bg-[#7228ee] disabled:bg-gray-300 transition-colors font-medium"
                >
                  Convert to {activeTab === 'json-to-csv' ? 'CSV' : 'JSON'}
                </button>
              </div>
            </motion.div>
          </div>

          {/* Output Section */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl sm:rounded-2xl shadow-lg border-2 border-gray-100 p-4 sm:p-6 lg:p-8"
            >
              <div className="flex items-center justify-between mb-4 sm:mb-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">
                  Output {activeTab === 'json-to-csv' ? 'CSV' : 'JSON'}
                </h3>
                <div className="flex space-x-1 sm:space-x-2">
                  {(activeTab === 'json-to-csv' ? csvOutput : jsonOutput) && (
                    <>
                      <button
                        onClick={copyOutput}
                        className="p-1.5 sm:p-2 text-gray-500 hover:text-[#8438ff] hover:bg-[#8438ff]/10 rounded-lg transition-colors"
                        title="Copy to clipboard"
                      >
                        <Copy className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                      <button
                        onClick={downloadOutput}
                        className="p-1.5 sm:p-2 text-gray-500 hover:text-green-500 hover:bg-green-50 rounded-lg transition-colors"
                        title="Download file"
                      >
                        <Download className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                    </>
                  )}
                </div>
              </div>

              <textarea
                value={activeTab === 'json-to-csv' ? csvOutput : jsonOutput}
                readOnly
                className="w-full h-64 sm:h-80 lg:h-96 p-3 sm:p-4 border-2 border-gray-200 rounded-lg sm:rounded-xl resize-none bg-gray-50 font-mono text-xs sm:text-sm"
                placeholder={`Converted ${activeTab === 'json-to-csv' ? 'CSV' : 'JSON'} will appear here...`}
              />

              {(activeTab === 'json-to-csv' ? csvOutput : jsonOutput) && (
                <div className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-600">
                  <p>Characters: {(activeTab === 'json-to-csv' ? csvOutput : jsonOutput).length}</p>
                  <p>Lines: {(activeTab === 'json-to-csv' ? csvOutput : jsonOutput).split('\n').length}</p>
                </div>
              )}
            </motion.div>
          </div>
        </div>

        {/* Footer Ad */}
        <AdContainer 
          position="footer" 
          adSlot={GOOGLE_ADSENSE_CONFIG.adSlots.imageConverter.footer}
          className="mt-8"
        />
      </div>
    </div>
  );
};

export default JsonCsvConverter;
